/**
 * Phase 66 Plan 06 Task 3 — Theme toggle button (D-10).
 *
 * Toggles `useDarkMode()`. Lucide Sun/Moon icons indicate the CURRENT
 * mode (sun = light, moon = dark). Clicking flips it.
 *
 * Used in two places:
 *   1. UserMenu dropdown (between user info and logout)
 *   2. SettingsPage "Preferenze" section
 *
 * The button preserves the testid contract `theme-toggle` for tests +
 * future Playwright hooks.
 */

import { useTranslation } from "react-i18next";
import { Moon, Sun } from "lucide-react";

import { useDarkMode } from "@/hooks/useDarkMode";

export type ThemeToggleProps = {
  /** Render style: "menu-item" inside a dropdown, "switch" as a standalone control. */
  variant?: "menu-item" | "switch";
  className?: string;
};

export default function ThemeToggle({ variant = "switch", className }: ThemeToggleProps) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useDarkMode();

  const Icon = enabled ? Moon : Sun;
  const label = enabled ? t("theme.dark") : t("theme.light");
  const ariaLabel = t("theme.toggle");

  if (variant === "menu-item") {
    return (
      <button
        type="button"
        data-testid="theme-toggle"
        onClick={() => setEnabled(!enabled)}
        className={
          className ??
          "flex w-full items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-[color:var(--color-bg)] rounded-sm"
        }
        aria-label={ariaLabel}
        aria-pressed={enabled}
      >
        <Icon size={16} aria-hidden="true" />
        <span>{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid="theme-toggle"
      onClick={() => setEnabled(!enabled)}
      className={
        className ??
        "inline-flex items-center gap-2 px-3 py-1.5 rounded border border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-sm hover:bg-[color:var(--color-bg)] cursor-pointer"
      }
      aria-label={ariaLabel}
      aria-pressed={enabled}
    >
      <Icon size={14} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
